import { getSessionDetail, groupSets, listNotes, listPhotos, PB_LABEL, plannedVsActual } from '@gymtv/data';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { fmtDate, fmtDuration, fmtVolume } from '../lib/format';
import { config, supabase } from '../lib/supabase';

export function SessionDetail() {
  const { id = '' } = useParams();
  const { units } = useAuth();
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof getSessionDetail>> | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<Awaited<ReturnType<typeof listPhotos>>>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getSessionDetail(supabase, id).then(setDetail, (e) => setError(e.message));
    listNotes(supabase, id).then(setNotes, () => {});
    listPhotos(supabase, id).then(setPhotos, () => {});
  }, [id]);

  async function exportFit() {
    setError(null);
    const { data } = await supabase.auth.getSession();
    const res = await fetch(`${config.supabaseUrl}/functions/v1/fit-export?session_id=${id}`, {
      headers: { Authorization: `Bearer ${data.session?.access_token}`, apikey: config.supabaseAnonKey },
    });
    if (!res.ok) return setError(`FIT export failed (${res.status}).`);
    const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'gymtv.fit';
    const url = URL.createObjectURL(await res.blob());
    const a = Object.assign(document.createElement('a'), { href: url, download: name });
    a.click();
    URL.revokeObjectURL(url);
  }

  if (!detail) return error ? <p className="error">{error}</p> : <p className="muted">Loading…</p>;
  const { summary, sets, pbs } = detail;
  return (
    <>
      <div className="page-head">
        <div className="stack" style={{ gap: 6 }}>
          <Link to="/history" className="muted">← History</Link>
          <h1>{summary.title}</h1>
          <p className="muted num">
            {fmtDate(summary.started_at)} · {fmtDuration(summary.duration_s)} · {summary.sets_done} sets · {fmtVolume(summary.volume, units)}
          </p>
        </div>
        <div className="actions">
          <button onClick={exportFit} title="Download a .fit file for Garmin Connect → Import Data">
            Export FIT
          </button>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {notes.session && <p className="card">📝 {notes.session}</p>}
      {pbs.length > 0 && (
        <div className="card stack" style={{ gap: 4 }}>
          <h3>Personal bests</h3>
          {pbs.map((p) => (
            <p key={p.exercise_key + p.kind}>
              {p.exercise_name}: {PB_LABEL[p.kind]} <b className="num">{p.value}{p.kind === 'max_reps' ? ' reps' : ` ${units}`}</b> <span className="muted">(was {p.previous_value})</span>
            </p>
          ))}
        </div>
      )}
      {groupSets(sets).map((g) => (
        <section key={g.key} className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th colSpan={4} style={{ textTransform: 'none', letterSpacing: 0, fontFamily: 'var(--display)', fontSize: '1.2rem', color: 'var(--fg)' }}>
                  {g.name}
                  {notes[`exercise:${g.key}`] && <span className="muted" style={{ fontFamily: 'var(--body)', fontSize: 14, fontWeight: 400 }}> · 📝 {notes[`exercise:${g.key}`]}</span>}
                </th>
              </tr>
              <tr>
                <th>Set</th>
                <th>Done</th>
                <th>Planned</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {g.sets.map((s, i) => (
                <tr key={s.id}>
                  <td className="num">{i + 1}</td>
                  <td className="num"><b>{s.actual_reps ?? '–'} × {s.actual_weight ?? '–'}{s.actual_weight != null ? ` ${units}` : ''}</b></td>
                  <td className="num muted">{plannedVsActual(s) ?? 'as planned'}</td>
                  <td>{notes[`set:${s.id}`] ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
      {photos.length > 0 && (
        <div className="row">
          {photos.map((p) => p.url && <img key={p.id} src={p.url} alt="Workout photo" style={{ width: 160, height: 160, objectFit: 'cover', borderRadius: 10 }} />)}
        </div>
      )}
    </>
  );
}
